<template>
    <div class="min-h-screen w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="container mx-auto">
            <div class="px-4 sm:px-8 flex flex-col w-full pb-4 sm:pb-8 mt-4 sm:mt-8">
                <div class="mb-4">
                    <bread-crumb :items="breadCrumbItems"></bread-crumb>
                </div>

                <section class="h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
                    <h1 class="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                        <font-awesome-icon :icon="['fas', 'users']" class="text-blue-600 mr-3" />
                        People Overview
                    </h1>

                    <div v-if="isLoading" class="flex justify-center py-12">
                        <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                    </div>

                    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <!-- Admins -->
                        <div
                            @click.stop="goToPeopleType('admins')"
                            class="bg-blue-50 border border-blue-200 rounded-xl p-5 shadow-sm hover:shadow-md transition cursor-pointer">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center space-x-4">
                                    <font-awesome-icon :icon="['fas', 'user-shield']" class="text-blue-600 text-3xl" />
                                    <div>
                                        <h2 class="text-lg font-medium text-gray-700">Admins</h2>
                                        <p class="text-2xl font-bold text-blue-800">{{ adminCount }}</p>
                                    </div>
                                </div>
                                <button
                                    v-if="adminBreakdown.length"
                                    @click.stop="showAdminBreakdown = !showAdminBreakdown"
                                    class="text-blue-600 hover:text-blue-800 transition"
                                >
                                    <font-awesome-icon
                                        :icon="['fas', showAdminBreakdown ? 'chevron-up' : 'chevron-down']"
                                    />
                                </button>
                            </div>

                            <transition name="fade-slide" mode="out-in">
                                <div
                                    v-if="showAdminBreakdown && adminBreakdown.length"
                                    class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700"
                                >
                                    <div
                                        v-for="item in adminBreakdown"
                                        :key="item.label"
                                        class="flex justify-between"
                                    >
                                        <span>{{ item.label }}</span>
                                        <span class="font-semibold">{{ item.count }}</span>
                                    </div>
                                </div>
                            </transition>
                        </div>

                        <!-- Academics -->
                        <div
                            @click.stop="goToPeopleType('academics')"
                            class="bg-green-50 border border-green-200 rounded-xl p-5 flex items-center justify-between shadow-sm hover:shadow-md transition cursor-pointer">
                            <div class="flex items-center space-x-4">
                                <font-awesome-icon
                                    :icon="['fas', 'chalkboard-teacher']"
                                    class="text-green-600 text-3xl"
                                />
                                <div>
                                    <h2 class="text-lg font-medium text-gray-700">Academics</h2>
                                    <p class="text-2xl font-bold text-green-800">{{ academicCount }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Academic Support -->
                        <div
                            @click.stop="goToPeopleType('academic-support')"
                            class="bg-teal-50 border border-teal-200 rounded-xl p-5 flex items-center justify-between shadow-sm hover:shadow-md transition cursor-pointer">
                            <div class="flex items-center space-x-4">
                                <font-awesome-icon
                                    :icon="['fas', 'handshake']"
                                    class="text-teal-600 text-3xl"
                                />
                                <div>
                                    <h2 class="text-lg font-medium text-gray-700">Academic Support</h2>
                                    <p class="text-2xl font-bold text-teal-800">{{ academicSupportCount }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Contractors -->
                        <div
                            @click.stop="goToPeopleType('contractors')"
                            class="bg-yellow-50 border border-yellow-200 rounded-xl p-5 flex items-center justify-between shadow-sm hover:shadow-md transition cursor-pointer">
                            <div class="flex items-center space-x-4">
                                <font-awesome-icon :icon="['fas', 'user-tie']" class="text-yellow-600 text-3xl" />
                                <div>
                                    <h2 class="text-lg font-medium text-gray-700">Contractors</h2>
                                    <p class="text-2xl font-bold text-yellow-800">{{ contractorCount }}</p>
                                    <p class="text-xs text-gray-500 mt-1">Coming soon</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import gql from "graphql-tag";
import BreadCrumb from "../../components/shared/BreadCrumb.vue";
import { ADPOLY_ENTITY, getCampusConfig, isValidCampus } from "./config";

export default {
    name: "adpoly-people-overview",
    components: {
        BreadCrumb,
    },
    props: ["campus"],
    data() {
        return {
            isLoading: false,
            showAdminBreakdown: false,
            adminCount: 0,
            academicCount: 0,
            academicSupportCount: 0,
            contractorCount: 0,
            adminBreakdown: [],
        };
    },
    created() {
        this.validateCampus();
        this.fetchPeopleSummary();
    },
    watch: {
        campus: function () {
            this.validateCampus();
            this.fetchPeopleSummary();
        },
    },
    computed: {
        campusConfig() {
            return getCampusConfig(this.campus);
        },
        campusTitle() {
            return this.campusConfig ? this.campusConfig.title : "";
        },
        campusCode() {
            return this.campusConfig ? this.campusConfig.campusCode : "";
        },
        breadCrumbItems() {
            const campusKey = this.campusConfig ? this.campusConfig.key : "";
            return [
                { label: "Dashboard", path: "/dashboard" },
                { label: "ADPOLY", path: "/adpoly" },
                {
                    label: this.campusTitle || "Campus",
                    path: campusKey ? "/adpoly/" + campusKey : "/adpoly",
                },
                {
                    label: "Campus",
                    path: campusKey ? "/adpoly/" + campusKey + "/campus" : "/adpoly",
                },
                { label: "People" },
            ];
        },
    },
    methods: {
        validateCampus() {
            if (!isValidCampus(this.campus)) {
                this.$router.push({ path: "/notFound" });
            }
        },
        goToPeopleType(peopleType) {
            if (!this.campusConfig) return;
            this.$router.push(
                "/adpoly/" + this.campusConfig.key + "/campus/people/" + peopleType
            );
        },
        async fetchFacultyTotal(facultyType) {
            const response = await this.$apollo.query({
                query: gql`
                    query GetFacultyCount(
                        $first: Int!
                        $page: Int!
                        $entity: String
                        $campus: String
                        $faculty_type: String
                    ) {
                        faculties(
                            first: $first
                            page: $page
                            entity: $entity
                            campus: $campus
                            faculty_type: $faculty_type
                        ) {
                            error
                            error_code
                            message
                            paginatorInfo {
                                total
                            }
                            data {
                                id
                                program
                                job_title
                                faculty_type
                            }
                        }
                    }
                `,
                variables: {
                    first: 1,
                    page: 1,
                    entity: ADPOLY_ENTITY,
                    campus: this.campusCode,
                    faculty_type: facultyType,
                },
                fetchPolicy: "network-only",
            });

            const result = response.data && response.data.faculties;
            if (!result || result.error) {
                throw new Error(
                    (result && result.message) || "Failed to fetch faculty count"
                );
            }

            return result.paginatorInfo && result.paginatorInfo.total
                ? result.paginatorInfo.total
                : 0;
        },
        async fetchAdminsForBreakdown() {
            const response = await this.$apollo.query({
                query: gql`
                    query GetAdminFaculties(
                        $first: Int!
                        $page: Int!
                        $entity: String
                        $campus: String
                        $faculty_type: String
                    ) {
                        faculties(
                            first: $first
                            page: $page
                            entity: $entity
                            campus: $campus
                            faculty_type: $faculty_type
                        ) {
                            error
                            error_code
                            message
                            paginatorInfo {
                                total
                            }
                            data {
                                id
                                program
                                job_title
                            }
                        }
                    }
                `,
                variables: {
                    first: 1000,
                    page: 1,
                    entity: ADPOLY_ENTITY,
                    campus: this.campusCode,
                    faculty_type: "admin",
                },
                fetchPolicy: "network-only",
            });

            const result = response.data && response.data.faculties;
            if (!result || result.error) {
                throw new Error(
                    (result && result.message) || "Failed to fetch admin faculties"
                );
            }

            return {
                total:
                    result.paginatorInfo && result.paginatorInfo.total
                        ? result.paginatorInfo.total
                        : 0,
                data: result.data || [],
            };
        },
        buildAdminBreakdown(admins) {
            const counts = {};

            admins.forEach(function (admin) {
                const label =
                    (admin.program && String(admin.program).trim()) ||
                    (admin.job_title && String(admin.job_title).trim()) ||
                    "Other";
                counts[label] = (counts[label] || 0) + 1;
            });

            return Object.keys(counts)
                .map(function (label) {
                    return { label: label, count: counts[label] };
                })
                .sort(function (a, b) {
                    return b.count - a.count;
                });
        },
        async fetchPeopleSummary() {
            if (!this.campusCode) return;

            this.isLoading = true;
            this.showAdminBreakdown = false;
            this.adminCount = 0;
            this.academicCount = 0;
            this.academicSupportCount = 0;
            this.adminBreakdown = [];

            try {
                const adminResult = await this.fetchAdminsForBreakdown();
                const academicTotal = await this.fetchFacultyTotal("academic");
                const academicSupportTotal = await this.fetchFacultyTotal("academic_support");

                this.adminCount = adminResult.total;
                this.academicCount = academicTotal;
                this.academicSupportCount = academicSupportTotal;
                this.adminBreakdown = this.buildAdminBreakdown(adminResult.data);
            } catch (error) {
                console.error("Failed to fetch ADPOLY people summary:", error);
                if (this.$swal) {
                    this.$swal.fire({
                        icon: "error",
                        title: "Oops...",
                        text:
                            (error && error.message) ||
                            "Failed to load people overview.",
                    });
                }
            }

            this.isLoading = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-5px);
}
</style>
